/**
 * Format a number as currency with appropriate formatting
 */
export const formatCurrency = (
  amount: number,
  currency: string = "CLP"
): string => {
  // For CLP, format without decimals
  if (currency === "CLP") {
    return new Intl.NumberFormat("es-CL", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  // For other currencies, use appropriate formatting
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a number as unit price with decimals for CLP
 */
export const formatUnitPrice = (
  amount: number,
  currency: string = "CLP"
): string => {
  // For CLP, format with 4 decimal places for unit prices
  if (currency === "CLP") {
    return new Intl.NumberFormat("es-CL", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    }).format(amount);
  }

  // For other currencies, use appropriate formatting
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
};

/**
 * Format a date string to a readable format
 */
export const formatDate = (
  dateString: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
): string => {
  if (!dateString) {
    return "Fecha no disponible";
  }

  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  return date.toLocaleDateString("es-CL", options);
};

export const calculateTotal = (
  items: { quantity: number; pricePerUnit: number }[]
): number => {
  return items.reduce(
    (total, item) => total + item.quantity * item.pricePerUnit,
    0
  );
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case "approved":
    case "accepted":
    case "cleared":
    case "good":
      return "bg-green-500";
    case "pending_approval":
    case "pending_review":
    case "pending":
    case "average":
      return "bg-amber-500";
    case "rejected":
    case "cancelled":
    case "late":
    case "poor":
      return "bg-red-500";
    default:
      return "bg-gray-500";
  }
};

export const getStatusText = (status: string): string => {
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

// Format RUT value to XXXXXXXX-X (without dots)
export const formatRut = (rut: string | null | undefined): string => {
  if (!rut) return "";

  // Remove any existing formatting
  const cleaned = rut.replace(/[^0-9kK]/g, "");

  if (cleaned.length <= 1) return cleaned;

  // Format as XXXXXXXX-X (without dots)
  const digits = cleaned.slice(0, -1);
  const verifier = cleaned.slice(-1);

  return `${digits}-${verifier}`;
};

// RUT body without separators or check digit, uppercased (e.g. "76.425.115-6" -> "76425115").
// Matches Softland's `softland_code` for Chilean suppliers, so it doubles as a dedup key.
export const rutBody = (rut: string | null | undefined): string =>
  (rut ?? "").replace(/[^0-9kK]/g, "").toUpperCase().slice(0, -1);

// Standard practical email pattern. Keep identical to EMAIL_RE in
// supabase/functions/_shared/email.ts (separate module systems, same rule).
export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// True when `email` looks like a real address (rejects junk like "NAN" and
// stray characters such as the pipe in "foo|@gmail.com").
export const isValidEmail = (email: string | null | undefined): boolean =>
  EMAIL_REGEX.test((email ?? "").trim());

// Validate a Chilean RUT check digit (módulo 11). Accepts any formatting.
export const isValidRut = (rut: string | null | undefined): boolean => {
  const cleaned = (rut ?? "").replace(/[^0-9kK]/g, "").toUpperCase();
  if (cleaned.length < 2) return false;

  const body = cleaned.slice(0, -1);
  const verifier = cleaned.slice(-1);
  if (!/^\d+$/.test(body)) return false;

  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = 11 - (sum % 11);
  const expected =
    remainder === 11 ? "0" : remainder === 10 ? "K" : String(remainder);

  return verifier === expected;
};
